/**
 * Console / structured-log helpers for ICE preflight. None of the code
 * here affects selection — it only formats existing data for human and
 * Sentry consumption. `ice-preflight.ts` calls into this file; the
 * dependency only goes in that direction.
 */

import type { IceSelection, IceUrlProbeResult } from './ice-preflight';

export type IceUrlScheme = 'stun' | 'turn' | 'turns';
export type IceUrlTransport = 'udp' | 'tcp';

export interface IceUrlInfo {
  scheme: IceUrlScheme;
  transport: IceUrlTransport;
  host: string;
  port: number;
}

const LOG_PREFIX = '[WebRTC ICE]';

function defaultPort(scheme: IceUrlScheme): number {
  return scheme === 'turns' ? 5349 : 3478;
}

/**
 * Parse a STUN/TURN URL into its scheme, transport, host, and port.
 * Implicit defaults per RFC 5389 / RFC 5928: bare `turn:` is UDP;
 * STUN/TURN default port is 3478, TURNS default port is 5349.
 */
export function parseIceUrl(url: string): IceUrlInfo | null {
  const match = /^(stuns?|turns?):([^?]+)(?:\?(.*))?$/i.exec(url.trim());
  if (!match) return null;
  const rawScheme = match[1].toLowerCase();
  const scheme: IceUrlScheme =
    rawScheme === 'stuns' || rawScheme === 'stun'
      ? 'stun'
      : rawScheme === 'turns'
        ? 'turns'
        : 'turn';
  const hostPort = match[2];
  const query = match[3] ?? '';

  let host: string;
  let port: number;
  const ipv6Match = /^\[([^\]]+)\](?::(\d+))?$/.exec(hostPort);
  if (ipv6Match) {
    host = ipv6Match[1];
    port = ipv6Match[2] ? Number(ipv6Match[2]) : defaultPort(scheme);
  } else {
    const lastColon = hostPort.lastIndexOf(':');
    if (lastColon === -1) {
      host = hostPort;
      port = defaultPort(scheme);
    } else {
      host = hostPort.slice(0, lastColon);
      const portCandidate = Number(hostPort.slice(lastColon + 1));
      port = Number.isFinite(portCandidate)
        ? portCandidate
        : defaultPort(scheme);
    }
  }

  const transportMatch = /transport=(udp|tcp)/i.exec(query);
  const transport: IceUrlTransport = transportMatch
    ? (transportMatch[1].toLowerCase() as IceUrlTransport)
    : scheme === 'turns'
      ? 'tcp'
      : 'udp';

  return { scheme, transport, host, port };
}

function urlsOf(server: RTCIceServer): string[] {
  if (Array.isArray(server.urls)) return server.urls;
  return [server.urls];
}

function singleUrlOf(server: RTCIceServer): string {
  return urlsOf(server)[0];
}

export function summarizeIceServers(servers: RTCIceServer[]): {
  total: number;
  stun: number;
  turn: number;
  turns: number;
  tcp: number;
  udp: number;
} {
  return servers.reduce(
    (summary, server) => {
      urlsOf(server).forEach((url) => {
        summary.total += 1;
        if (/^stun:/i.test(url)) summary.stun += 1;
        if (/^turns?:/i.test(url)) summary.turn += 1;
        if (/^turns:/i.test(url)) summary.turns += 1;
        if (/transport=tcp/i.test(url)) summary.tcp += 1;
        else if (/^(stuns?|turns?):/i.test(url)) summary.udp += 1;
      });
      return summary;
    },
    { total: 0, stun: 0, turn: 0, turns: 0, tcp: 0, udp: 0 },
  );
}

export function consoleLogIceServers(
  label: string,
  servers: RTCIceServer[],
): void {
  console.info(`${LOG_PREFIX} ${label}`, summarizeIceServers(servers));
}

function listIceServerUrls(servers: RTCIceServer[]): string[] {
  return servers.flatMap((server) => urlsOf(server));
}

export function consoleLogIceSelection(selection: IceSelection): void {
  console.info(`${LOG_PREFIX} selected ICE servers`, {
    profileId: selection.profileId,
    reason: selection.reason,
    iceTransportPolicy: selection.iceTransportPolicy,
    summary: summarizeIceServers(selection.iceServers),
    urls: listIceServerUrls(selection.iceServers),
  });
}

export function consoleLogPreflightStart(targets: RTCIceServer[]): void {
  console.info(
    `${LOG_PREFIX} starting URL-level preflight`,
    targets.map((server) => {
      const url = singleUrlOf(server);
      return { url, info: parseIceUrl(url) };
    }),
  );
}

export function consoleLogProbeResult(result: IceUrlProbeResult): void {
  console.info(`${LOG_PREFIX} preflight URL complete`, {
    url: result.url,
    transport: parseIceUrl(result.url)?.transport ?? null,
    elapsedMs: result.elapsedMs,
    timedOut: result.timedOut,
    errorCount: result.errorCount,
    candidates: {
      host: result.counters.host,
      srflx: result.counters.srflx,
      prflx: result.counters.prflx,
      relay: result.counters.relay,
      udp: result.counters.udp,
      tcp: result.counters.tcp,
    },
  });
}
